import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import Header from 'components/Header'
import Footer from 'components/Footer'
import BackButton from 'components/BackButton'
import SearchResult from 'components/SearchResult'
import styles from './StaticFileLayout.module.sass'
import { getPrivacySectionConfig } from 'modules/bootstrap/BootstrapSelector'
import { getSearchTerm } from 'modules/home/HomeSelector'

import config from 'site.config'

const { sections, configuration } = config

const crypto = window.crypto || window.msCrypto
let random = new Uint32Array(1)

const getLoadingHtml = title => `
  <h1>${title}</h1>
  <div style="width:-webkit-fill-available;margin-bottom:40px;">
    <span aria-live="polite" aria-busy="true">
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
      <span class="react-loading-skeleton">‌</span><br>
    </span>
  </div>
`

const StaticFileLayout = ({ title, filename }) => {
  const searchTerm = useSelector(getSearchTerm)
  const [menuOpen, setMenuOpen] = useState(false)

  const [loading, setLoading] = useState(true)
  const [html, setHTML] = useState('')

  const privacySection = useSelector(getPrivacySectionConfig)

  const privacyReplacements = useMemo(
    () => ({
      serviceName:
        privacySection?.service_name || sections.privacy.service_name,
      brandName: privacySection?.brand_name || sections.privacy.brand_name,
      address: privacySection?.address || sections.privacy.address,
      email: privacySection?.email || sections.privacy.email
    }),
    [privacySection]
  )

  const baseUrl =
    process.env.NODE_ENV === 'production'
      ? `${configuration?.envVars?.REACT_APP_CDN_URL}/${configuration.assetPath}`
      : window.location.origin + '/cdn'

  const isSearching = Boolean(searchTerm?.length)

  const closeMenu = useCallback(() => {
    setMenuOpen(crypto?.getRandomValues(random)[0])
  }, [])

  const closeMenuOnKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' || e.key === ' ')
        closeMenu(crypto?.getRandomValues(random)[0])
    },
    [closeMenu]
  )

  // Load HTML file
  useEffect(() => {
    const scriptId = `staticfile-${filename}-js`
    let didCancel = false

    setLoading(true)

    fetch(`${baseUrl}/${filename}.html`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        return response.text()
      })
      .then(data => {
        if (didCancel) return
        const hydrated = data
          .replaceAll('{service_name}', privacyReplacements.serviceName)
          .replaceAll('{brand_name}', privacyReplacements.brandName)
          .replaceAll('{address}', privacyReplacements.address)
          .replaceAll('{email}', privacyReplacements.email)
        setHTML(hydrated)

        // Load JS only after HTML is loaded
        const existing = document.getElementById(scriptId)
        if (existing) existing.remove()

        const script = document.createElement('script')
        script.id = scriptId
        script.src = `${baseUrl}/${filename}.js`
        script.async = true
        document.body.appendChild(script)
        setLoading(false)
      })
      .catch(() => {
        if (didCancel) return
        setHTML(`<h1>Failed to load ${title}!</h1>`)
        setLoading(false)
      })

    return () => {
      didCancel = true
      const existing = document.getElementById(scriptId)
      if (existing) existing.remove()
    }
  }, [baseUrl, filename, title, privacyReplacements])

  // Load CSS file
  useEffect(() => {
    const link = document.createElement('link')
    link.href = `${baseUrl}/${filename}.css`
    link.setAttribute('rel', 'stylesheet')
    link.setAttribute('media', 'print')
    link.onload = () => {
      link.setAttribute('media', 'all')
    }
    document.head.appendChild(link)
    return () => link.remove()
  }, [baseUrl, filename])

  const renderedHtml = loading ? getLoadingHtml(title) : html

  return (
    <div className={styles.box}>
      <Header menuOpen={menuOpen} />
      <div className={styles.gap}></div>
      {isSearching ? (
        <div
          role="button"
          tabIndex={0}
          data-testid="search"
          className={styles.content}
          onClick={closeMenu}
          onKeyDown={closeMenuOnKeyDown}
        >
          <SearchResult />
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          data-testid="content"
          className={styles.content}
          onClick={closeMenu}
          onKeyDown={closeMenuOnKeyDown}
        >
          <div dangerouslySetInnerHTML={{ __html: renderedHtml }} />
          <BackButton />
        </div>
      )}
      <Footer setMenuOpen={closeMenu} />
    </div>
  )
}

StaticFileLayout.propTypes = {
  title: PropTypes.string.isRequired,
  filename: PropTypes.string.isRequired
}
export default StaticFileLayout
